import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  ChevronDown,
  Sparkles,
  Clock,
  Home,
  CheckCircle2,
  RotateCcw,
} from 'lucide-react';
import { Button } from './ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/DropdownMenu';
import { QuickTip } from './ui/Tooltip';
import {
  api,
  ApiError,
  type BuildRun,
  type IndexingEventStatus,
} from '../api/client';
import { useToast } from './Toast';
import { formatRelative } from './SiteCard';

interface Props {
  projectId: string;
  disabled?: boolean;
  disabledReason?: string;
  /**
   * Latest BuildRun for this project. When status is 'running' or 'queued'
   * the button disables itself with a tooltip — submitting to Google while
   * a build is in flight (e.g. a connect-domain metadata_only re-render)
   * pings stale snapshots, which is what bit us at 00:31 production.
   * The auto-ping job re-fires once the build completes, so the operator
   * doesn't lose anything by waiting.
   */
  buildRun?: BuildRun;
}

type Mode = 'auto' | 'all' | 'stale' | 'homepage';

const MODE_LABEL: Record<Mode, string> = {
  auto: 'Smart ping',
  all: 'Full re-index',
  stale: 'Stale pages only',
  homepage: 'Homepage only',
};

/**
 * The engine writes 'ok' on a successful index submission (see
 * packages/db/schema.ts → IndexingEventStatus). Keep this constant in lock-step
 * with that union so the post-submission status pill never silently disappears
 * again.
 */
const OK_STATUS: IndexingEventStatus = 'ok';

export function IndexButton({ projectId, disabled, disabledReason, buildRun }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const buildInFlight =
    buildRun?.status === 'running' || buildRun?.status === 'queued';
  const effectivelyDisabled = disabled || buildInFlight;
  const effectiveDisabledReason = buildInFlight
    ? 'Indexing will run automatically when the build finishes.'
    : disabledReason;

  const { data: eventsData } = useQuery({
    queryKey: ['indexEvents', projectId],
    queryFn: () => api.indexEvents(projectId, 50),
    enabled: !effectivelyDisabled,
    refetchInterval: (q) => (busy ? 3000 : q.state.data?.events.length ? 30000 : 15000),
    retry: false,
  });

  const events = eventsData?.events ?? [];
  const successful = events.filter((e) => e.status === OK_STATUS);
  const lastSuccess = successful[0];
  const googleCount = successful.filter((e) => e.provider === 'google').length;
  const indexnowCount = successful.filter((e) => e.provider === 'indexnow').length;
  const hasSubmissions = successful.length > 0;

  async function ping(mode: Mode): Promise<void> {
    setBusy(true);
    try {
      await api.indexPing(projectId, mode);
      toast.success(`Submitted to Google + IndexNow (${MODE_LABEL[mode]})`);
      // events show up async — refetch a few times so the "submitted" pill
      // appears within a few seconds.
      setTimeout(() => qc.invalidateQueries({ queryKey: ['indexEvents', projectId] }), 1500);
      setTimeout(() => qc.invalidateQueries({ queryKey: ['indexEvents', projectId] }), 5000);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        toast.show('Indexing API not yet available — wiring in progress', 'info');
      } else if (err instanceof ApiError && err.status === 409) {
        toast.show(err.message || 'Site not ready for indexing yet', 'info');
      } else {
        const msg = err instanceof ApiError ? err.message : String(err);
        toast.error(`Indexing failed: ${msg}`);
      }
    } finally {
      setBusy(false);
    }
  }

  if (effectivelyDisabled) {
    return (
      <QuickTip content={effectiveDisabledReason ?? 'Available once the site is live'}>
        <span className="inline-flex">
          <Button
            variant="outline"
            size="md"
            disabled
            leftIcon={<Search className="h-3.5 w-3.5" />}
          >
            Submit to Google
          </Button>
        </span>
      </QuickTip>
    );
  }

  // After the first successful submission we collapse the CTA into a
  // status pill + "Re-submit" dropdown. The user asked for the Submit
  // button to "not exist" once a site has been submitted — this gives
  // them a confirmation surface plus an escape hatch if Google misses
  // something and they want to re-ping.
  if (hasSubmissions) {
    return (
      <div className="inline-flex items-stretch gap-2">
        <div className="inline-flex items-center gap-2 h-9 rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-emerald-900">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" />
          <span className="text-[12.5px] font-medium tracking-tightish">
            Submitted to Google
          </span>
          <span className="text-[11px] text-emerald-700/80">
            {lastSuccess ? formatRelative(lastSuccess.createdAt) : ''}
          </span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="md"
              aria-label="Re-submit"
              disabled={busy}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Re-submit</span>
              <ChevronDown className="h-3 w-3 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[260px]">
            <DropdownMenuLabel>Re-submit to Google + IndexNow</DropdownMenuLabel>
            <div className="px-2.5 pb-2 text-[11px] text-zinc-500 space-y-0.5">
              <div>
                Google submissions:{' '}
                <span className="font-medium text-zinc-700">{googleCount}</span>
              </div>
              <div>
                IndexNow submissions:{' '}
                <span className="font-medium text-zinc-700">{indexnowCount}</span>
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => void ping('stale')}>
              <Clock className="h-3.5 w-3.5" />
              <div className="flex flex-col">
                <span>Stale pages only</span>
                <span className="text-[11px] text-zinc-500">Cheapest — only 30d+ stale URLs</span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void ping('homepage')}>
              <Home className="h-3.5 w-3.5" />
              <div className="flex flex-col">
                <span>Homepage only</span>
                <span className="text-[11px] text-zinc-500">Quick recrawl signal</span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void ping('all')}>
              <Search className="h-3.5 w-3.5" />
              <div className="flex flex-col">
                <span>Full re-index</span>
                <span className="text-[11px] text-zinc-500">Every URL — burns Google quota</span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  // First-time submit state
  return (
    <div className="inline-flex rounded-lg shadow-card">
      <Button
        variant="primary"
        size="md"
        loading={busy}
        onClick={() => void ping('auto')}
        leftIcon={<Search className="h-3.5 w-3.5" />}
        className="rounded-r-none border-r border-white/10 shadow-none"
      >
        Submit to Google
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="primary"
            size="md"
            aria-label="Indexing options"
            className="rounded-l-none px-2 shadow-none"
            disabled={busy}
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[240px]">
          <DropdownMenuLabel>Indexing mode</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => void ping('auto')}>
            <Sparkles className="h-3.5 w-3.5" />
            <div className="flex flex-col">
              <span>Smart ping</span>
              <span className="text-[11px] text-zinc-500">New + changed pages</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => void ping('all')}>
            <Search className="h-3.5 w-3.5" />
            <div className="flex flex-col">
              <span>Full re-index</span>
              <span className="text-[11px] text-zinc-500">Every URL in the sitemap</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => void ping('stale')}>
            <Clock className="h-3.5 w-3.5" />
            <div className="flex flex-col">
              <span>Stale only</span>
              <span className="text-[11px] text-zinc-500">Pages not pinged in 30+ days</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => void ping('homepage')}>
            <Home className="h-3.5 w-3.5" />
            <div className="flex flex-col">
              <span>Homepage only</span>
              <span className="text-[11px] text-zinc-500">Quick recrawl signal</span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
